import React from 'react'

import { fill } from '@/lib/i18n/fill'
import type { Board, Entry, Person } from '@/lib/leaderboard'
import { cn } from '@/lib/utils'

import { AppContext } from '@/components/base/appcontext'
import CoachBadge from '@/components/base/coachBadge'
import LinkWithState from '@/components/base/link'
import { LIST_ROW_PRESS } from '@/components/base/listRow'
import UserAvatar from '@/components/base/userAvatar'
import { Typography } from '@/components/ui/typography'

import { ClipboardList, Dumbbell, Sprout, type LucideIcon } from 'lucide-react'

// Medal fills for the top three, light and dark. Literal class names so
// Tailwind picks them up.
const MEDALS: Record<number, string> = {
    1: 'bg-amber-400 text-amber-950',
    2: 'bg-zinc-300 text-zinc-900 dark:bg-zinc-400',
    3: 'bg-orange-700 text-orange-50'
}

// What a number counts: workouts (athletes), plan requests (coaches), or
// people who joined (newcomers, shown as "+N").
type Stat = Board | 'joined'

const STATS: Record<
    Stat,
    { Icon: LucideIcon; slug: string; tint: string; prefix?: string }
> = {
    athletes: {
        Icon: Dumbbell,
        slug: 'leaderboard.stat.workouts',
        tint: '[&>svg]:text-muted-foreground'
    },
    coaches: {
        Icon: ClipboardList,
        slug: 'leaderboard.stat.requests',
        tint: '[&>svg]:text-muted-foreground'
    },
    joined: {
        Icon: Sprout,
        slug: 'leaderboard.newcomers.count',
        tint: 'text-emerald-700 dark:text-emerald-300',
        prefix: '+'
    }
}

/** A number with the icon of what it counts, read out in full. */
export function StatValue({ stat, value }: { stat: Stat; value: number }) {
    const appContext = React.useContext(AppContext)
    const { Icon, slug, tint, prefix = '' } = STATS[stat]
    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center gap-1.5 tabular-nums',
                tint
            )}
            aria-label={fill(appContext.geti18n(slug), { n: value })}
        >
            <Icon aria-hidden className="h-4 w-4" />
            <span className="text-base font-semibold leading-none">
                {prefix}
                {value}
            </span>
        </span>
    )
}

/** The rank: a medal for the top three, a plain number after. */
export function RankBadge({ rank }: { rank: number }) {
    return (
        <span
            className={cn(
                'shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-mono tabular-nums text-sm font-semibold',
                MEDALS[rank] ?? 'text-muted-foreground'
            )}
            aria-label={`#${rank}`}
        >
            {rank}
        </span>
    )
}

/**
 * One person on a board: rank, avatar, login and their count when ranked;
 * someone only active in the window gets the "active" label instead. Opens
 * the profile.
 */
export function LeaderboardRow({
    person,
    board,
    self
}: {
    person: Entry | Person
    board: Board
    /** The caller's own row: highlighted. */
    self?: boolean
}) {
    const appContext = React.useContext(AppContext)
    const t = appContext.geti18n.bind(appContext)
    // Only the coaches board lists coaches (a public coach profile); a
    // level alone, like a paid "pro" athlete, is not one.
    const coach = board === 'coaches'
    const ranked = 'rank' in person
    return (
        <LinkWithState
            to={`/coaches/${person.id}`}
            className={cn(
                'flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5',
                LIST_ROW_PRESS,
                self && 'border-primary/40 bg-primary/[0.04]'
            )}
        >
            {ranked ? (
                <RankBadge rank={person.rank} />
            ) : (
                <span aria-hidden className="w-7 shrink-0" />
            )}
            <UserAvatar
                name={person.login}
                photo={person.photo}
                coach={coach}
                className="h-9 w-9 shrink-0"
                tintKey={person.id}
                fallbackClassName="text-sm font-semibold"
            />
            <div className="flex min-w-0 flex-1 items-center gap-2">
                <Typography variant="title-sm" className="truncate" dir="auto">
                    {person.login}
                </Typography>
                {coach && (
                    <CoachBadge className="shrink-0">
                        {t('leaderboard.coach.badge')}
                    </CoachBadge>
                )}
            </div>
            {ranked ? (
                <StatValue stat={board} value={person.value} />
            ) : (
                <span className="shrink-0 text-xs font-medium text-muted-foreground">
                    {t('leaderboard.active.label')}
                </span>
            )}
        </LinkWithState>
    )
}
